import React, { useMemo } from "react"
import { Table, Tag, Space, Typography, message, Avatar } from "@mimber/ui-web"
import { UserOutlined, ClockCircleOutlined } from "@mimber/ui-web"
import { useGroupMembers } from "@mimber/client/mimber-organization"
import { useNavigate } from "@tanstack/react-router"
import { GroupMemberActionsDropdown } from "@/components/dropdowns"
import type { ColumnsType, TableProps } from "@mimber/ui-web"
import type { GroupMemberWithRelationsDto } from "@mimber/schema"
import type { GroupMembersTableProps } from "./GroupMembersTable.types"
import {
  handleTableChange,
  applyQueryParamsToColumns,
  getColumnSearchProps,
  formatTableDate,
} from "@mimber/ui-web"
import { useRememberedTableQueryParams } from "@/core/hooks"
import {
  getRememberedPaginationConfig,
  getRememberedTableLocale,
} from "@/utils"

const { Text } = Typography

export const GroupMembersTable: React.FunctionComponent<
  GroupMembersTableProps
> = ({ groupUuid, onEditMember, className, ...tableProps }) => {
  const navigate = useNavigate()

  const { queryParams, setQueryParams, resetQueryParams } =
    useRememberedTableQueryParams({
      tableId: "group-members",
      defaultQueryParams: {
        page: 1,
        limit: 10,
      },
      scope: {
        groupUuid,
      },
    })

  const {
    data: response,
    isLoading,
    error,
  } = useGroupMembers(groupUuid, queryParams)

  // Show error message if query fails
  React.useEffect(() => {
    if (error) {
      message.error(`Failed to load group members: ${error.message}`)
    }
  }, [error])

  const columns: ColumnsType<GroupMemberWithRelationsDto> = useMemo(
    () => [
      {
        title: "Member",
        key: "member",
        render: (_, record) => (
          <Space>
            <Avatar icon={<UserOutlined />} size="default" />
            <div>
              <div>
                <Text strong>
                  {record.membership.user.firstName &&
                  record.membership.user.lastName
                    ? `${record.membership.user.firstName} ${record.membership.user.lastName}`
                    : record.membership.user.email}
                </Text>
              </div>
              <div>
                <Text type="secondary" style={{ fontSize: "12px" }}>
                  {record.membership.user.email}
                </Text>
              </div>
            </div>
          </Space>
        ),
        sorter: true,
        sortDirections: ["ascend", "descend"],
        ...getColumnSearchProps("Search member"),
      },
      {
        title: "Role",
        key: "role",
        render: (_, record) => {
          if (!record.groupRole) {
            return <Tag>Member</Tag>
          }
          return (
            <Tag color={record.groupRole.isLeader ? "blue" : "default"}>
              {record.groupRole.name}
              {record.groupRole.isLeader && " (Leader)"}
            </Tag>
          )
        },
        filters: [
          { text: "Leaders", value: "leader" },
          { text: "Members", value: "member" },
        ],
      },
      {
        title: "Title",
        dataIndex: "title",
        key: "title",
        render: (title: string | null) => (
          <Text type={title ? "secondary" : undefined}>
            {title || "No title"}
          </Text>
        ),
      },
      {
        title: "Joined",
        dataIndex: "createdAt",
        key: "createdAt",
        sorter: true,
        sortDirections: ["ascend", "descend"],
        render: (createdAt: string) => (
          <Space>
            <ClockCircleOutlined />
            <Text type="secondary">{formatTableDate(createdAt)}</Text>
          </Space>
        ),
      },
      {
        title: "Actions",
        key: "actions",
        fixed: "right",
        width: 80,
        render: (_, record) => (
          <div onClick={(event) => event.stopPropagation()}>
            <GroupMemberActionsDropdown
              member={record}
              groupUuid={groupUuid}
              onEditMember={(member) => onEditMember?.(member)}
            />
          </div>
        ),
      },
    ],
    [groupUuid, onEditMember]
  )

  const displayColumns = useMemo(
    () => applyQueryParamsToColumns(columns, queryParams),
    [columns, queryParams]
  )

  const handleRowClick = (record: GroupMemberWithRelationsDto) => {
    navigate({
      to: "/members/$memberId",
      params: { memberId: record.membership.uuid },
    })
  }

  const onTableChange: TableProps<GroupMemberWithRelationsDto>["onChange"] = (
    pagination,
    filters,
    sorter
  ) => {
    const tableChangeData = {
      pagination,
      filters: filters as Record<string, string[] | null>,
      sorter: {
        field: Array.isArray(sorter) ? sorter[0]?.field : sorter?.field,
        order: Array.isArray(sorter) ? sorter[0]?.order : sorter?.order,
      } as { field?: string | string[]; order?: "ascend" | "descend" },
    }
    handleTableChange(tableChangeData, setQueryParams, columns)
  }

  const paginationConfig = getRememberedPaginationConfig(
    response?.meta?.pagination?.total || 0,
    queryParams,
    resetQueryParams
  )

  return (
    <>
      <Table<GroupMemberWithRelationsDto>
        bordered
        columns={displayColumns}
        dataSource={response?.data || []}
        loading={isLoading}
        rowKey="uuid"
        pagination={paginationConfig}
        locale={getRememberedTableLocale(resetQueryParams, queryParams)}
        onChange={onTableChange}
        onRow={(record) => ({
          onClick: () => handleRowClick(record),
          style: { cursor: "pointer" },
        })}
        className={className}
        {...tableProps}
      />
    </>
  )
}
